/**
 * Инициализация моста web ↔ native и системные подписки, не требующие роутера
 * (init / locale / theme). Back-навигация и canGoBack живут в
 * `WebViewBridgeProvider`, т.к. им нужен доступ к react-router.
 *
 * Всё под гейтом `isInWebView`: в обычном браузере `setupBridge()` — no-op,
 * текущее поведение не меняется.
 */
import { Languages, SUPPORTED_LANGUAGES } from '@/constants';
import i18next from '@/i18n';
import { safeLocalStorage } from '@/services/helpers/DOM';
import { getAppNavigate } from '@/services/helpers/navigate-helper';
import { dispatch } from '@/store';

import { clientApi } from '../../api';
import {
  setClientCity,
  setIsInWebView,
  setTheme,
  setWebviewProperties
} from '../../client.store';
import { setIsAppAuthenticated } from './base-actions';
import { type InitContext, SYSTEM_EVENTS, bridge } from './vendor/web';

/** Класс на <html>, под который вешается webview-специфичный CSS (safe-area). */
export const WEBVIEW_ROOT_CLASS = 'is-webview';

/**
 * Нормализуем локаль от нативы к поддерживаемым приложением кодам.
 * Натива может прислать ISO-код ('kk', 'ru-RU', 'en-US') — берём базовый
 * сегмент и маппим казахский 'kk' → 'kz' (внутренний код приложения).
 */
const normalizeLocale = (locale: string): Languages | null => {
  const base = locale.toLowerCase().split(/[-_]/)[0];
  const mapped = base === 'kk' ? Languages.kz : (base as Languages);
  return SUPPORTED_LANGUAGES.includes(mapped) ? mapped : null;
};

/**
 * Сменить язык приложения БЕЗ перехода по URL (WebView не перезагружается,
 * адресная строка с локалью остаётся прежней — на неё завязаны QR-коды).
 */
const applyLocale = (locale: string): void => {
  const lang = normalizeLocale(locale);
  if (lang && i18next.language !== lang) i18next.changeLanguage(lang);
};

/** Прокидываем safe-area insets от нативы в CSS-переменные. */
const applyInsets = (insets: InitContext['insets']): void => {
  const root = document.documentElement.style;
  root.setProperty('--sai-top', `${insets.top}px`);
  root.setProperty('--sai-right', `${insets.right}px`);
  root.setProperty('--sai-bottom', `${insets.bottom}px`);
  root.setProperty('--sai-left', `${insets.left}px`);
};

/** Добавляем viewport-fit=cover, чтобы env(safe-area-inset-*) заработал под вырезами. */
const ensureViewportFitCover = (): void => {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="viewport"]');
  if (!meta || /viewport-fit/.test(meta.content)) return;
  meta.content = `${meta.content}, viewport-fit=cover, user-scalable=no, maximum-scale=1.0, minimum-scale=1.0`;
};

const clearInsets = (): void => {
  const root = document.documentElement.style;
  root.removeProperty('--sai-top');
  root.removeProperty('--sai-right');
  root.removeProperty('--sai-bottom');
  root.removeProperty('--sai-left');
};

const applyTheme = (theme: 'light' | 'dark'): void => {
  dispatch(setTheme(theme));
  const themeColorMeta = document.querySelector<HTMLMetaElement>(
    'meta[name="theme-color"]'
  );
  if (themeColorMeta)
    themeColorMeta.content = theme === 'dark' ? '#000000' : '#ffffff';
  const colorSchemeMeta = document.querySelector<HTMLMetaElement>(
    'meta[name="color-scheme"]'
  );
  if (colorSchemeMeta) colorSchemeMeta.content = 'light dark';
  safeLocalStorage.set('native-theme', theme);
};

const navigateToInitialPath = async (initialPath?: string) => {
  if (!initialPath) return;
  if (!initialPath.includes('create')) {
    const isSsoAuthorized = await clientApi.ssoAuthorize();
    if (!isSsoAuthorized) return;
  }
  const navigate = getAppNavigate();
  navigate(initialPath, { replace: true });
};

/**
 * Подключить мост и системные подписки. Вызывается один раз при монтировании
 * клиентского модуля.
 *
 * @returns teardown — снять подписки и освободить мост (вызвать при размонтировании).
 */
export function setupBridge(onFinishSetup?: () => void) {
  // init — первое событие после bridge.ready. Несёт контекст приложения.
  // Буферизуется нативной стороной, поэтому подписка до bridge.init() ничего не теряет.
  // Деградации в браузерный режим нет: если натива не пришлёт init, приложение
  // остаётся в ожидании, но веб-версией для нативки не подменяется.
  const offInit = bridge.on<InitContext>(SYSTEM_EVENTS.Init, async (ctx) => {
    setIsAppAuthenticated(ctx.auth.isAuthenticated);
    // Webview-косметику применяем после подтверждённого handshake.
    document.documentElement.classList.add(WEBVIEW_ROOT_CLASS);
    ensureViewportFitCover();
    const locale = ctx.locale === 'kk' ? Languages.kz : ctx.locale;
    applyLocale(locale);
    applyInsets(ctx.insets);
    dispatch(setIsInWebView(true));
    applyTheme(ctx.mode);
    // Свойства от нативки (город и пр.) кладём в стор «как есть», без запросов.
    dispatch(setWebviewProperties(ctx.webProperties));
    if (ctx.webProperties?.city)
      dispatch(setClientCity(ctx.webProperties.city));

    await navigateToInitialPath(ctx.webProperties?.initialPath);
    onFinishSetup?.();
  });

  const offTheme = bridge.on<'light' | 'dark'>(
    SYSTEM_EVENTS.ThemeChanged,
    (mode) => dispatch(setTheme(mode))
  );

  // Смена языка из приложения — переключаем i18next без смены URL.
  const offLocale = bridge.on<{ locale: string }>(
    SYSTEM_EVENTS.LocaleChanged,
    ({ locale }) => applyLocale(locale)
  );

  bridge.init({
    targetOrigin: process.env.REACT_APP_PUBLIC_PWA_URL,
    allowedOrigins: [
      process.env.REACT_APP_PUBLIC_PWA_URL || '',
      process.env.REACT_APP_NOMAD_LEASING_URL || ''
    ].filter(Boolean)
  }); // подключается к __nativeBridge и шлёт bridge.ready

  return () => {
    offInit();
    offTheme();
    offLocale();
    bridge.destroy();
    document.documentElement.classList.remove(WEBVIEW_ROOT_CLASS);
    clearInsets();
    dispatch(setIsInWebView(false));
  };
}
